import React, { useState, useEffect, useImperativeHandle, forwardRef, useCallback } from 'react';
import LazyImage from './LazyImage';

const Gallery = forwardRef(({ images, sectionName, maxDisplay, thumbnailSize }, ref) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [preloadedImages, setPreloadedImages] = useState(new Set());
  const [showAll, setShowAll] = useState(false);

  // Expose methods to parent components
  useImperativeHandle(ref, () => ({
    openGallery: (index = 0) => {
      setCurrentIndex(index);
      setIsOpen(true);
    },
    closeGallery: () => {
      setIsOpen(false);
    }
  }));

  // Close gallery on escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const openGallery = (index) => {
    setCurrentIndex(index);
    setIsOpen(true);
  };

  const closeGallery = () => {
    setIsOpen(false);
  };

  const nextImage = () => {
    setCurrentIndex((prev) => (prev + 1) % images.length);
  };

  const prevImage = () => {
    setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  const goToImage = (index) => {
    setCurrentIndex(index);
  };

  const handleImageError = (index) => {
  };

  // Preload adjacent images when lightbox opens
  const preloadImage = useCallback((index) => {
    if (preloadedImages.has(index) || !images[index]) return;
    
    const img = new Image();
    img.src = images[index].src;
    img.onload = () => {
      setPreloadedImages(prev => new Set([...prev, index]));
    };
  }, [images, preloadedImages]);

  // Preload current and adjacent images
  useEffect(() => {
    if (isOpen) {
      preloadImage(currentIndex);
      preloadImage(currentIndex - 1);
      preloadImage(currentIndex + 1);
    }
  }, [isOpen, currentIndex, preloadImage]);

  if (!images || images.length === 0) {
    return null;
  }

  // Determine which images to display in the grid
  const hasMaxDisplay = maxDisplay && maxDisplay > 0 && maxDisplay < images.length;
  const displayedImages = hasMaxDisplay && !showAll ? images.slice(0, maxDisplay) : images;
  const hiddenCount = hasMaxDisplay && !showAll ? images.length - maxDisplay : 0;

  // Create custom grid style for thumbnail sizing
  const gridStyle = thumbnailSize ? {
    gridTemplateColumns: `repeat(auto-fit, minmax(${thumbnailSize}px, ${thumbnailSize}px))`,
    justifyContent: 'center'
  } : {};

  const toggleShowAll = () => {
    setShowAll(!showAll);
  };

  return (
    <>
      {/* Gallery Thumbnails */}
      <div className="gallery-container">
        <h3 className="gallery-title">{sectionName}</h3>
        <div className="gallery-grid" style={gridStyle}>
          {displayedImages.map((image, index) => (
            <div
              key={index}
              className="gallery-thumbnail"
              onClick={() => openGallery(index)}
            >
              <LazyImage
                src={image.src}
                alt={image.alt || `${sectionName} ${index + 1}`}
                className="gallery-thumbnail-image"
                onError={() => handleImageError(index)}
              />
              <div className="gallery-overlay">
                <i className="fas fa-search-plus"></i>
              </div>
            </div>
          ))}
        </div>
        
        {/* Show More/Less Button */}
        {hasMaxDisplay && (
          <div className="gallery-show-more">
            <button className="gallery-show-more-btn" onClick={toggleShowAll}>
              {showAll ? (
                <>
                  <i className="fas fa-chevron-up"></i>
                  Show Less
                </>
              ) : (
                <>
                  <i className="fas fa-chevron-down"></i>
                  Show {hiddenCount} More Photo{hiddenCount !== 1 ? 's' : ''}
                </>
              )}
            </button>
          </div>
        )}
      </div>

      {/* Lightbox Modal */}
      {isOpen && (
        <div className="lightbox-overlay" onClick={closeGallery}>
          <div className="lightbox-container" onClick={(e) => e.stopPropagation()}>
            {/* Close Button */}
            <button className="lightbox-close" onClick={closeGallery}>
              <i className="fas fa-times"></i>
            </button>

            {/* Navigation Arrows */}
            {images.length > 1 && (
              <>
                <button className="lightbox-nav lightbox-prev" onClick={prevImage}>
                  <i className="fas fa-chevron-left"></i>
                </button>
                <button className="lightbox-nav lightbox-next" onClick={nextImage}>
                  <i className="fas fa-chevron-right"></i>
                </button>
              </>
            )}

            {/* Main Image */}
            <div className="lightbox-image-container">
              <img
                key={currentIndex} // Force re-render when index changes
                src={images[currentIndex].src}
                alt={images[currentIndex].alt || `${sectionName} ${currentIndex + 1}`}
                className="lightbox-image"
                onError={() => handleImageError(currentIndex)}
              />
              {images[currentIndex].caption && (
                <div className="lightbox-caption">
                  {images[currentIndex].caption}
                </div>
              )}
            </div>

            {/* Thumbnail Navigation */}
            {images.length > 1 && (
              <div className="lightbox-thumbnails">
                {images.map((image, index) => (
                  <div
                    key={index}
                    className={`lightbox-thumb ${index === currentIndex ? 'active' : ''}`}
                    onClick={() => goToImage(index)}
                  >
                    <img
                      src={image.src}
                      alt={image.alt || `${sectionName} ${index + 1}`}
                      className="lightbox-thumb-image"
                      onError={() => handleImageError(index)}
                    />
                  </div>
                ))}
              </div>
            )}

            {/* Image Counter */}
            <div className="lightbox-counter">
              {currentIndex + 1} / {images.length}
            </div>
          </div>
        </div>
      )}
    </>
  );
});

Gallery.displayName = 'Galllllllery';

export default Gallery;
